import React, { useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface Building {
  id: string;
  name: string;
  address: string;
  totalParkingSpots: number;
  availableSpots: number;
  isActive: boolean;
  users?: any[];
}

const BuildingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form] = Form.useForm();
  const [usersForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['buildings', includeInactive],
    queryFn: async () => {
      const response = await api.get(`/admin/buildings?includeInactive=${includeInactive}`);
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: (values: Partial<Building>) => api.post('/admin/buildings', values),
    onSuccess: () => {
      message.success(t('buildings.added'));
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error(t('buildings.addError')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<Building> }) => api.put(`/admin/buildings/${id}`, values),
    onSuccess: () => {
      message.success(t('buildings.updated'));
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      setIsModalOpen(false);
      setEditingBuilding(null);
      form.resetFields();
    },
    onError: () => message.error(t('buildings.updateError')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/buildings/${id}`),
    onSuccess: () => {
      message.success(t('buildings.deleted'));
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
    },
    onError: () => message.error(t('buildings.deleteError')),
  });

  const assignUsersMutation = useMutation({
    mutationFn: ({ buildingId, userIds }: { buildingId: string; userIds: string[] }) =>
      api.post(`/admin/buildings/${buildingId}/users`, { userIds }),
    onSuccess: () => {
      message.success(t('buildings.usersAssigned'));
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
        setIsUsersModalOpen(false);
      },
      onError: () => message.error(t('buildings.assignUsersError')),
  });

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (editingBuilding) {
        updateMutation.mutate({ id: editingBuilding.id, values });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleEdit = (building: Building) => {
    setEditingBuilding(building);
    form.setFieldsValue(building);
    setIsModalOpen(true);
  };

  const handleAssignUsers = (building: Building) => {
    setSelectedBuilding(building);
    usersForm.setFieldsValue({ userIds: building.users?.map((u: any) => u.id) || [] });
    setIsUsersModalOpen(true);
  };

  const columns = [
    { title: t('buildings.name'), dataIndex: 'name', key: 'name' },
    { title: t('buildings.address'), dataIndex: 'address', key: 'address' },
    {
      title: t('buildings.parkingSpots'),
      key: 'parking',
      render: (_: any, record: Building) => (
        <span>{record.availableSpots} / {record.totalParkingSpots}</span>
      ),
    },
    {
      title: t('buildings.active'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: Building) => (
        <Switch checked={isActive} onChange={(checked) => updateMutation.mutate({ id: record.id, values: { isActive: checked } })} />
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: Building) => (
        <Space>
          <Button icon={<TeamOutlined />} size="small" onClick={() => handleAssignUsers(record)} title={t('buildings.assignUsers')} />
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title={t('buildings.deleteConfirm')} onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>{t('buildings.title')}</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingBuilding(null); form.resetFields(); setIsModalOpen(true); }}>
          {t('buildings.addBuilding')}
        </Button>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Switch checked={includeInactive} onChange={(checked) => setIncludeInactive(checked)} />
        <span style={{ marginLeft: 8 }}>{t('buildings.showInactive')}</span>
      </div>
      <Table dataSource={data?.buildings || []} columns={columns} rowKey="id" loading={isLoading} />

      <Modal title={editingBuilding ? t('buildings.editBuilding') : t('buildings.addBuilding')} open={isModalOpen} onOk={handleOk} onCancel={() => { setIsModalOpen(false); setEditingBuilding(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t('buildings.name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label={t('buildings.address')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="totalParkingSpots" label={t('buildings.totalParkingSpots')} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="availableSpots" label={t('buildings.availableSpots')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={t('buildings.assignUsers')} open={isUsersModalOpen} onCancel={() => setIsUsersModalOpen(false)}
        onOk={() => {
          usersForm.validateFields().then((values) => {
            assignUsersMutation.mutate({ buildingId: selectedBuilding!.id, userIds: values.userIds || [] });
          });
        }}
      >
        <Form form={usersForm} layout="vertical">
          <Form.Item name="userIds" label={t('buildings.users')}>
            <SelectUsers form={usersForm} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const SelectUsers: React.FC<{ form: any }> = ({ form }) => {
  const { t } = useTranslation();
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/admin/users');
      return response.data;
    }
  });

  return (
    <Select
      mode="multiple"
      placeholder={t('buildings.selectUsers')}
      style={{ width: '100%' }}
      value={form.getFieldValue('userIds') || []}
      onChange={(values) => form.setFieldsValue({ userIds: values })}
    >
      {usersData?.users?.map((user: any) => (
        <Select.Option key={user.id} value={user.id}>
          {user.firstName} {user.lastName} ({user.role})
        </Select.Option>
      ))}
    </Select>
  );
};

export default BuildingsPage;